/**
 * Process-step reference classification (§4.13 / §10.3).
 *
 * `classifyCallee` is the **single source of truth** for how a process
 * step's callee resolves. Validation, stub synthesis, and graph-edge
 * derivation all route through it so the three independent step
 * traversals can never disagree about whether a callee is a real
 * interface leaf or a module whose handler interface is unspecified
 * (and therefore synthesized as the anonymous TODO leaf).
 *
 * Pure: no model mutation, no diagnostics — callers decide what a
 * classification means (an edge, a stub, a TODO).
 */

import type { SimpleStep } from "./types.js";
import { ANONYMOUS_INTERFACE_NAME } from "./types.js";

// NOTE: the `do`-template-caller substitution now lives on the resolved IR as
// `substituteTemplateCallerIR` (process-ir-walk.ts) — the raw-AST version was
// removed in the IR-convergence cleanup once graph + walker stopped re-walking
// the AST. `classifyCallee` below stays: it is the single callee-classification
// source the builder bakes into the IR's `ResolvedCallee`.

/**
 * Minimal lookup the classifier needs. Both a `ReadonlySet<string>`
 * (validation / stubs) and a `Map<string, …>` (graph indexes) satisfy
 * the `{ has }` shape, so one function serves every caller.
 */
export interface CalleeIndex {
  readonly modules: { has(key: string): boolean };
  readonly interfaces: { has(key: string): boolean };
  /** v0.9.0 §1.6: module key → its default interface leaf. A bare module callee
   *  with no trailing dot resolves through this to the declared default instead
   *  of a synthesized `(anonymous)` TODO leaf. */
  readonly defaultInterfaceOf?: { get(key: string): string | undefined };
}

export type CalleeClassification =
  | {
      /** Callee is (or stubs to) a real interface leaf. */
      readonly kind: "interface";
      readonly interfaceSegs: readonly string[];
    }
  | {
      /**
       * Callee deduces a module whose handler interface is unspecified;
       * a synthesized anonymous interface (`module.(anonymous)`) stands
       * in as a TODO.
       */
      readonly kind: "module-anon";
      readonly moduleSegs: readonly string[];
      readonly interfaceSegs: readonly string[];
    };

/**
 * Classify a step's callee `to` path against the known module/interface
 * symbols. Order matters:
 *
 *   1. A declared interface leaf wins — unless the author explicitly left
 *      the interface unspecified with a trailing dot (`> Payments.`).
 *   2. An unspecified callee (trailing dot), a declared module used where
 *      an interface belongs, or a single bare segment (which can never be
 *      an interface leaf — interfaces are always owned by a module) all
 *      deduce a MODULE whose handler interface is synthesized as the
 *      anonymous TODO leaf.
 *   3. Otherwise the terminal segment is an (as-yet-undeclared) interface
 *      leaf and the prefix is its owning module — the ordinary stub path.
 */
export function classifyCallee(step: SimpleStep, index: CalleeIndex): CalleeClassification {
  const to = step.to;
  const toKey = to.join(".");

  if (!step.toUnspecified && index.interfaces.has(toKey)) {
    return { kind: "interface", interfaceSegs: to };
  }

  if (step.toUnspecified || index.modules.has(toKey) || to.length === 1) {
    // v0.9.0 §1.6: a module callee resolves to its DEFAULT interface when it
    // declares one — mature, no TODO. The trailing-dot pin (`> B.`) always
    // stays unspecified, so it never reaches the default.
    const defaultLeaf = step.toUnspecified ? undefined : index.defaultInterfaceOf?.get(toKey);
    if (defaultLeaf !== undefined) {
      return { kind: "interface", interfaceSegs: [...to, defaultLeaf] };
    }
    return {
      kind: "module-anon",
      moduleSegs: to,
      interfaceSegs: [...to, ANONYMOUS_INTERFACE_NAME],
    };
  }

  return { kind: "interface", interfaceSegs: to };
}
